import React, { useEffect, useState } from "react";
import AdminSidebar from "../../components/partial/AdminNavbar";
import FileUploader from "../../components/common/FileUploader";
import { useDispatch, useSelector } from "react-redux";
import {
  addNotice,
  deleteNoticeData,
  getNoticeData,
} from "../../store/notices/reducer";
import { Toaster, toast } from "sonner";
import { BsThreeDotsVertical } from "react-icons/bs";
import { useNavigate } from "react-router-dom";
import Button from "../../components/common/Button";

const Notice = () => {
  const dispatch = useDispatch();
  const [showModal, setShowModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false); // Controls modal visibility
  const [currentNotice, setCurrentNotice] = useState(null); // Holds current notice details
  const [isPopupChecked, setIsPopupChecked] = useState(false); // State for the popup checkbox
  const [newNotice, setNewNotice] = useState({
    title: "",
    details: "",
    popup: false,
    days: "",
  });
  const [uploadedFiles, setUploadedFiles] = useState([]); // Store uploaded files
  const [openEdit, setOpenEdit] = useState(null); // Track which dropdown is open
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedPopup, setSelectedPopup] = useState("");

  const navigate = useNavigate();
  const rawNotice = useSelector((state) => state.noticeReducer.noticeData);
  const noticeData = (rawNotice && rawNotice.data) || [];
  console.log("[Notice] raw notice object from store:", rawNotice);
  console.log("[Notice] noticeData array extracted:", noticeData);
  // Manually define month names
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const handleCheckboxChange = (e) => {
    setIsPopupChecked(e.target.checked);
    const checked = e.target.checked;
    setNewNotice((prev) => ({
      ...prev,
      popup: checked,
      days: checked ? prev.days : "", // Clear days if popup is unchecked
    }));
  };

  const handleFilesUploaded = (files) => {
    setUploadedFiles([...files]);
  };

  // Helper function to format the date
  const formatDate = (date) => {
    const options = { year: "numeric", month: "long", day: "numeric" };
    return new Date(date).toLocaleDateString("en-US", options);
  };

  // Helper function to get the month and year as a string
  const getMonthYear = (date) => {
    const options = { year: "numeric", month: "long" };
    return new Date(date).toLocaleDateString("en-US", options);
  };

  const openModal = () => {
    setCurrentNotice(null);
    setNewNotice({ title: "", details: "" });
    setShowAddModal(true);
  };

  const closeModal = () => {
    setShowAddModal(false);
    setCurrentNotice(null);
    setNewNotice({ title: "", details: "" });
  };

  const openShowModal = (notice) => {
    setCurrentNotice(notice);
    setShowModal(true);
  };

  const handleCreateNotice = (e) => {
    e.preventDefault();

    const formData = new FormData();
    formData.append("title", newNotice.title);
    formData.append("details", newNotice.details);
    formData.append("popup", isPopupChecked);

    if (newNotice.popup && newNotice.days) {
      formData.append("days", newNotice.days);
    }

    if (uploadedFiles) {
      for (let i = 0; i < uploadedFiles.length; i++) {
        formData.append("photos", uploadedFiles[i]);
      }
    }

    (async () => {
      try {
        await dispatch(addNotice(formData));
        toast.success("Notice created successfully");
        closeModal();
        dispatch(getNoticeData());
      } catch (err) {
        console.error(err);
        toast.error("Failed to create notice");
      }
    })();
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this notice?")) return;
    try {
      await dispatch(deleteNoticeData(id));
      toast.success("Notice deleted successfully");
      dispatch(getNoticeData());
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete notice");
    }
  };

  const handleEdit = (title, id) => {
    navigate(`/nvm-admin/notice/edit/${encodeURIComponent(title)}`, {
      state: { id },
    });
    dispatch(getNoticeData);
  };

  // Group notices by month and year
  const groupNoticesByMonthYear = (notices) => {
    if (!Array.isArray(notices)) return {};
    return notices.reduce((acc, notice) => {
      const monthYear = getMonthYear(notice.createdAt);
      if (!acc[monthYear]) {
        acc[monthYear] = [];
      }
      acc[monthYear].push(notice);

      return acc;
    }, {});
  };

  const [noticesByMonthYear, setNoticesByMonthYear] = useState(() =>
    groupNoticesByMonthYear(noticeData),
  );
  // Prevent from displaying same month  added
  const getUniqueMonthsAndYears = (notices) => {
    const months = new Set();
    const years = new Set();
    if (Array.isArray(notices)) {
      notices.forEach((notice) => {
        const date = new Date(notice.createdAt);

        const year = date.getFullYear();
        const month = date.toLocaleString("default", { month: "long" });

        months.add(month);
        years.add(year);
      });
    }

    return {
      months: Array.from(months),
      years: Array.from(years),
    };
  };

  const { months, years } = getUniqueMonthsAndYears(noticeData);

  // Filter notices based on month, year, and popup status
  const filteredGroupNotices = Object.keys(noticesByMonthYear).reduce(
    (acc, monthYear) => {
      const [month, year] = monthYear.split(" ");
      const monthMatches = selectedMonth ? month === selectedMonth : true;
      const yearMatches = selectedYear ? year === selectedYear : true;

      // Get notices for the current monthYear
      const noticesForMonthYear = noticesByMonthYear[monthYear];

      // Filter individual notices by popup status
      const filteredNoticesForMonthYear = noticesForMonthYear.filter(
        (notice) => {
          const popupMatches =
            selectedPopup === ""
              ? true
              : selectedPopup === "Popup"
                ? notice.popup === true
                : selectedPopup === "Not Popup"
                  ? notice.popup === false
                  : true;

          return monthMatches && yearMatches && popupMatches;
        },
      );

      // If there are any notices after filtering, add them to the result
      if (filteredNoticesForMonthYear.length > 0) {
        acc[monthYear] = filteredNoticesForMonthYear;
      }

      return acc;
    },
    {},
  );

  // Fetch notices once on mount
  useEffect(() => {
    getNoticeData(dispatch);
  }, [dispatch]);

  // Update grouped notices when noticeData changes (do NOT re-fetch here)
  useEffect(() => {
    const grouped = groupNoticesByMonthYear(noticeData);
    console.log("[Notice] grouping notices ->", {
      noticeDataLength: noticeData.length,
      grouped,
    });
    setNoticesByMonthYear(grouped);
  }, [noticeData]);

  console.log("Current notice ", currentNotice);
  return (
    <div className="flex flex-col md:flex-row md:items-center bg-gray-100 min-h-screen">
      <div className="lg:w-1/4 bg-white shadow-md">
        <AdminSidebar />
      </div>
      <Toaster position="top-right" richColors />

      <div className="flex-1 p-6 flex-row">
        <div className="border-b-4 w-full border-black">
          <h2 className="text-3xl font-semibold text-gray-800 mb-6 text-center">
            Notice Board
          </h2>
          <div className="flex items-center justify-center">
            <Button
              type="primary"
              className="mb-6 py-3  px-6"
              onClick={() => openModal()}
            >
              Add New Notice
            </Button>
          </div>

          {/* Date Selection */}
          <div className="lg:flex justify-center items-end mb-4 grid grid-rows-3 space-y-5">
            {/* Year */}
            <div className="mx-4 text-md rounded-lg border-2 border-[#1e81b0]  ">
              <select
                className="rounded-lg p-2 font-semibold"
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
              >
                <option value="">Select Year</option>
                {years.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
            {/* Month */}
            <div className="mx-4 text-md rounded-lg border-2 border-[#1e81b0]  ">
              <select
                className="rounded-lg p-2 font-semibold"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
              >
                <option value="">Select Month</option>
                {monthNames.map((month) => (
                  <option key={month} value={month}>
                    {month}
                  </option>
                ))}
              </select>
            </div>

            {/*Dropdown for filtering popup*/}
            <div className="mx-4 text-md rounded-lg border-2 border-[#1e81b0]  ">
              <select
                className="rounded-lg p-2 font-semibold"
                value={selectedPopup}
                onChange={(e) => setSelectedPopup(e.target.value)}
              >
                <option value="">Select Popup</option>
                <option>Popup</option>
                <option>Not Popup</option>
              </select>
            </div>
          </div>
        </div>
        <h2 className="text-xl text-center text-gray-700 font-semibold m-10">
          Note: <span className="bg-green-300 rounded-full">Green Color</span>{" "}
          indicates notices that are currently active in the popup.
        </h2>

        {/* Adding while modal is on */}
        {showAddModal && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center p-4"
            onClick={closeModal}
          >
            <div
              className="bg-white p-4 sm:p-6 rounded-lg w-11/12 max-w-xl sm:max-w-lg max-h-[90vh] overflow-y-auto relative"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="absolute top-2 right-2 text-gray-600 hover:text-gray-900 text-2xl"
                onClick={closeModal}
              >
                &times;
              </button>

              <h2 className="text-2xl font-semibold text-[#1e81b0] mb-4">
                Create New Notice
              </h2>

              <form>
                <input
                  type="text"
                  className="w-full p-3 border-gray-300   rounded-md focus:outline-none focus:ring-2 focus:ring-[#1e81b0] transition duration-200 mb-4"
                  placeholder="Notice Title"
                  value={newNotice.title}
                  onChange={(e) =>
                    setNewNotice({ ...newNotice, title: e.target.value })
                  }
                  required
                />
                <textarea
                  className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1e81b0] transition duration-200 mb-4"
                  placeholder="Notice Details"
                  value={newNotice.details}
                  onChange={(e) =>
                    setNewNotice({ ...newNotice, details: e.target.value })
                  }
                  required
                />

                <input
                  type="checkbox"
                  name="popup"
                  checked={newNotice.popup}
                  onChange={handleCheckboxChange}
                />
                <label className="ml-2">Popup</label>

                {newNotice.popup && (
                  <input
                    type="number"
                    min="1"
                    className="w-full p-3 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-600 transition duration-200 mb-4"
                    placeholder="Enter number of days to show Popup"
                    value={newNotice.days}
                    onChange={(e) =>
                      setNewNotice({ ...newNotice, days: e.target.value })
                    }
                    required
                  />
                )}

                <FileUploader
                  onFilesUploaded={handleFilesUploaded}
                  allowMultiple={true}
                />

                <Button
                  type="submit"
                  onClick={handleCreateNotice}
                  className="w-full py-3 mt-2 bg-[#1e81b0] text-white rounded-md hover:bg-[#1384ba] transition-all duration-300 ease-in-out"
                >
                  Create Notice
                </Button>
              </form>
            </div>
          </div>
        )}
        {/* Modals */}
        {showModal && currentNotice && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 p-4"
            onClick={() => setShowModal(false)}
          >
            <div
              className="bg-white rounded-lg shadow-lg w-11/12 max-w-xl sm:max-w-lg max-h-[90vh] overflow-y-auto p-4 sm:p-6 relative animate-fadeIn"
              onClick={(e) => e.stopPropagation()} // Prevent closing on click inside modal
            >
              {/* Modal Header */}
              <div
                key={currentNotice._id}
                className="flex justify-between items-center border-b pb-4 mb-4"
              >
                <h3 className="text-2xl font-bold text-gray-800">
                  {currentNotice.title}
                  {currentNotice.popup}
                </h3>

                {/* pop expiry notice */}
                {currentNotice.popup === true && (
                  <h3 className="text-2xl font-bold text-gray-800">
                    {new Date(
                      currentNotice.popupActivatedAt,
                    ).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </h3>
                )}

                <button
                  className="text-gray-500 hover:text-red-500 text-2xl"
                  onClick={() => setShowModal(false)}
                >
                  &times;
                </button>
              </div>

              {/* Modal Content modal*/}
              <div className="modal-content space-y-4">
                {currentNotice.photos && (
                  <img
                    src={`${currentNotice.photos[0]}`}
                    alt="Notice"
                    className="w-full h-auto rounded-md shadow-md"
                  />
                )}
                <p className="text-gray-700 text-lg">{currentNotice.details}</p>
              </div>

              {/* Modal Footer */}
              <div className="flex justify-end pt-4 mt-4 border-t">
                <button
                  className="bg-[#1e81b0] text-white px-6 py-2 rounded-lg shadow hover:bg-[#1384ba] transition duration-300"
                  onClick={() => setShowModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Content */}
        {Object.keys(filteredGroupNotices).map((monthYear) => (
          <div
            key={monthYear}
            className="w-full flex flex-col justify-center items-center space-y-4 mb-6 mt-4"
          >
            <h3 className="text-2xl font-semibold text-gray-800">
              {monthYear}
            </h3>
            {filteredGroupNotices[monthYear].map((notice, index) => (
              <div
                key={index}
                className="w-full md:w-1/2 bg-white p-4 rounded-md shadow-lg flex justify-between items-center"
                onMouseLeave={() => setOpenEdit(null)}
              >
                <div className="flex flex-col">
                  <h4
                    className="text-xl font-semibold text-gray-800 cursor-pointer flex items-center space-x-2"
                    onClick={() => openShowModal(notice)}
                  >
                    <div className="flex flex-col">{notice.title}</div>
                    {notice.popup && (
                      <div className="bg-green-400 rounded-full h-3 w-3"></div>
                    )}
                  </h4>

                  <p className="text-gray-600">
                    {formatDate(notice.createdAt)}
                  </p>
                </div>

                <div className="relative">
                  <BsThreeDotsVertical
                    size={20}
                    className="cursor-pointer"
                    onClick={
                      () =>
                        setOpenEdit(openEdit === notice._id ? null : notice._id) // Toggle dropdown based on _id
                    }
                  />
                  {openEdit === notice._id && (
                    <div className="absolute right-0 bg-white border rounded-md shadow-md mt-2 py-2 w-48 z-10">
                      <button
                        onClick={() => handleEdit(notice.title, notice._id)}
                        className="w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-200"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(notice._id)}
                        className="w-full text-red-500 text-left px-4 py-2 hover:bg-gray-200"
                      >
                        Delete
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

export default Notice;
